import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Btn, Loading, Msg, Section, SubTabs, fmtDate, panelGet, panelPost } from './kit';
import { AdminAccounts, AdminHistory } from './AdminAccountsPanel';
import { type AdminTargetIn } from './adminNav';
import { ADMIN_ACCESS_CHANGED_EVENT, KNOWN_SUPER_ADMINS, useAdminAccess } from './adminAccess';
import {
  AdminPicker, ChangedLine, ModeSwitch, Tag, emailsOf, readAdmins, sameList, type AdminOption,
} from './accessKit';

/**
 * /admin → Admins & access (group key `access`): Admin accounts and History
 * (AdminAccountsPanel.tsx — who is an admin or a read-only team member, who
 * added them), then Admin tabs (below). Every admin, team accounts included,
 * can open all three and see everything; only super admins get the controls
 * (the server refuses everyone else's changes with 403).
 *
 * The super admins decide, tab by tab, which admin groups and sub-tabs the
 * rest of the admin team sees; anything new stays hidden from other admins
 * until a super admin enables it. That is all this page does: every admin
 * group, with the controlled sub-tabs under it, each Hidden / Selected
 * admins / All admins.
 *
 * Who may open the member pages still being tested is not here: each lives
 * in its own group's Feature access tab (For You & What's New, Ocean;
 * FeatureAccessCard), super admins only. Old links to tab=features are
 * redirected there by the admin shell.
 *
 * Backend/admin_access.py through the panel dispatcher, section "access"
 * (access_state: every admin; access_set: super admins only, 403 otherwise):
 *   GET  access/access_state  {admins: [{email, name, active}],
 *                              groups: [{key, title, description, mode, admins, default_mode, is_new}],
 *                              tabs: [{key: "group.tab", group, title, mode, admins, default_mode}],
 *                              updated_by, updated_at, ...}   (its `features` are not used here)
 *   POST access/access_set    {unit: "group"|"tab", key, mode, admins?}
 *                             (admins only with "selected"; left out = keep the stored list)
 * Rows carry their own updated_by / updated_at; super_admins lists the fixed
 * super admins (marked, never offered in the pickers).
 */

type UnitMode = 'none' | 'selected' | 'all';
const UNIT_MODES: UnitMode[] = ['none', 'selected', 'all'];
/** Groups "All admins" leaves read-only team accounts out of (Backend/admin_accounts.py TEAM_NOT_IN_ALL). */
const TEAM_NOT_IN_ALL = ['database', 'system'];
const MODE_LABEL: Record<UnitMode, string> = { none: 'Hidden', selected: 'Selected admins', all: 'All admins' };
const MODE_OPTIONS = UNIT_MODES.map(m => ({ value: m, label: MODE_LABEL[m] }));
const modeOf = (v: any, fallback: UnitMode): UnitMode => (UNIT_MODES.includes(v) ? v : fallback);

interface Unit {
  unit: 'group' | 'tab';
  key: string;
  group: string;
  title: string;
  description: string;
  mode: UnitMode;
  admins: string[];
  defaultMode: UnitMode;
  isNew: boolean;
  updatedBy: string;
  updatedAt: string;
}

const str = (v: any) => (typeof v === 'string' ? v : '');
const humanTab = (key: string) => {
  const t = key.split('.').slice(1).join('.').replace(/_/g, ' ');
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : key;
};

function readUnits(data: any): { groups: Unit[]; tabs: Unit[] } {
  const d = data && typeof data === 'object' ? data : {};
  const groups: Unit[] = (Array.isArray(d.groups) ? d.groups : [])
    .filter((g: any) => g && typeof g.key === 'string')
    .map((g: any) => {
      const defaultMode = modeOf(g.default_mode, 'all');
      return {
        unit: 'group' as const, key: g.key, group: g.key,
        title: str(g.title) || g.key, description: str(g.description),
        mode: modeOf(g.mode, defaultMode), admins: emailsOf(g.admins), defaultMode,
        isNew: g.is_new === true || defaultMode === 'none',
        updatedBy: str(g.updated_by), updatedAt: str(g.updated_at),
      };
    });
  const tabs: Unit[] = (Array.isArray(d.tabs) ? d.tabs : [])
    .filter((t: any) => t && typeof t.key === 'string')
    .map((t: any) => {
      const defaultMode = modeOf(t.default_mode, 'all');
      const group = str(t.group) || t.key.split('.')[0];
      return {
        unit: 'tab' as const, key: t.key, group,
        title: str(t.title) || humanTab(t.key), description: str(t.description),
        mode: modeOf(t.mode, defaultMode), admins: emailsOf(t.admins), defaultMode,
        isNew: t.is_new === true || defaultMode === 'none',
        updatedBy: str(t.updated_by), updatedAt: str(t.updated_at),
      };
    });
  return { groups, tabs };
}

/** Can this admin open the unit under its saved rule? */
const opens = (u: Pick<Unit, 'mode' | 'admins'>, email: string) =>
  u.mode === 'all' || (u.mode === 'selected' && u.admins.includes(email));

const UnitRow: React.FC<{
  u: Unit; admins: AdminOption[]; nested?: boolean;
  /** For a sub-tab: its group is hidden from everyone, so the tab is too. */
  parentHidden?: boolean;
  viewAs: string; viewAsOpens?: boolean;
  /** Not a super admin: the rule is shown, the controls are disabled. */
  readOnly?: boolean;
  onSaved: (u: Unit, mode: UnitMode, picked: string[]) => void;
  onError: (text: string) => void;
}> = ({ u, admins, nested, parentHidden, viewAs, viewAsOpens, readOnly, onSaved, onError }) => {
  const [mode, setMode] = useState<UnitMode>(u.mode);
  const [picked, setPicked] = useState<string[]>(u.admins);
  const [busy, setBusy] = useState(false);
  const savedKey = `${u.mode}|${u.admins.join(',')}|${u.updatedAt}`;
  useEffect(() => { setMode(u.mode); setPicked(u.admins); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedKey]);

  const dirty = mode !== u.mode || (mode === 'selected' && !sameList(picked, u.admins));
  const save = async () => {
    setBusy(true);
    // `admins` only with Selected: left out, the server keeps the stored list
    // for the next time the unit goes back to Selected.
    const { error } = await panelPost('access', 'access_set',
      { unit: u.unit, key: u.key, mode, ...(mode === 'selected' ? { admins: picked } : {}) });
    setBusy(false);
    if (error) { onError(`${u.title}: ${error}`); return; }
    onSaved(u, mode, picked);
  };

  const who = u.mode === 'all'
    ? (TEAM_NOT_IN_ALL.includes(u.key) ? 'Every admin sees it; team accounts only when picked by name.' : 'Every admin sees it.')
    : u.mode === 'none' ? 'Only super admins see it.'
      : `Super admins and ${u.admins.length || 'no'} picked admin${u.admins.length === 1 ? '' : 's'} see it.`;

  return (
    <div className={`flex flex-col gap-2 py-3 ${nested ? 'pl-6 pr-3 border-l-2 border-primary-100 ml-3' : 'px-3'}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex flex-col min-w-[14rem] flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className={`${nested ? 'text-[13px]' : 'text-sm'} font-semibold text-grayscale-1300`}>
              {nested && <span className="text-grayscale-1100 font-normal">Sub-tab · </span>}{u.title}
            </span>
            {u.isNew && <Tag tone="new" title="New: hidden from other admins until enabled">New</Tag>}
            {u.mode === 'none' && <Tag tone="grey">Hidden</Tag>}
            {u.mode === 'selected' && <Tag tone="amber">{u.admins.length} admin{u.admins.length === 1 ? '' : 's'}</Tag>}
            {viewAs && (viewAsOpens
              ? <Tag tone="green" title={`${viewAs} can open this`}>Visible to them</Tag>
              : <Tag tone="grey" title={`${viewAs} cannot open this`}>Not visible to them</Tag>)}
          </span>
          {u.description && <span className="text-xs text-grayscale-1100 line-clamp-2">{u.description}</span>}
          <span className="text-xs text-grayscale-1100">
            {who}
            {parentHidden && ' The group itself is hidden, so nobody but super admins reaches this sub-tab.'}
          </span>
          {(u.updatedBy || u.updatedAt || u.mode !== u.defaultMode) && (
            <ChangedLine by={u.updatedBy} at={u.updatedAt} isDefault={u.mode === u.defaultMode} />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ModeSwitch<UnitMode> label={`Who sees ${u.title}`} value={mode} options={MODE_OPTIONS}
            disabled={busy || readOnly} onChange={setMode} />
          {dirty && !readOnly && (
            <>
              <Btn small kind="primary" disabled={busy || (mode === 'selected' && !picked.length)} onClick={save}
                title={mode === 'selected' && !picked.length ? 'Pick at least one admin' : undefined}>
                {busy ? 'Saving…' : 'Save'}
              </Btn>
              <Btn small disabled={busy} onClick={() => { setMode(u.mode); setPicked(u.admins); }}>Cancel</Btn>
            </>
          )}
        </div>
      </div>
      {mode === 'selected' && (
        <AdminPicker admins={admins} value={picked} onChange={setPicked} disabled={busy || readOnly} />
      )}
    </div>
  );
};

const AdminTabs: React.FC<{
  data: any; loading: boolean; reload: () => void; admins: AdminOption[];
}> = ({ data, loading, reload, admins }) => {
  const { superAdmin } = useAdminAccess();
  const readOnly = !superAdmin;
  const { groups, tabs } = useMemo(() => readUnits(data), [data]);
  const [q, setQ] = useState('');
  const [show, setShow] = useState<'all' | 'hidden' | 'new' | 'selected'>('all');
  const [viewAs, setViewAs] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const tabsOf = useMemo(() => {
    const m: Record<string, Unit[]> = {};
    tabs.forEach(t => { (m[t.group] = m[t.group] || []).push(t); });
    return m;
  }, [tabs]);

  const counts = useMemo(() => ({
    none: groups.filter(g => g.mode === 'none').length,
    selected: groups.filter(g => g.mode === 'selected').length,
    all: groups.filter(g => g.mode === 'all').length,
  }), [groups]);

  const matches = (u: Unit) => {
    const s = q.trim().toLowerCase();
    const textHit = !s || u.title.toLowerCase().includes(s) || u.description.toLowerCase().includes(s)
      || u.key.toLowerCase().includes(s);
    const showHit = show === 'all' || (show === 'hidden' && u.mode === 'none')
      || (show === 'new' && u.isNew) || (show === 'selected' && u.mode === 'selected');
    return textHit && showHit;
  };

  const viewAsGroups = viewAs ? groups.filter(g => opens(g, viewAs)).length : 0;
  const lastBy = str(data?.updated_by);
  const lastAt = str(data?.updated_at);

  const saved = (u: Unit, mode: UnitMode, picked: string[]) => {
    setMsg({ kind: 'ok', text: mode === 'none'
      ? `${u.title} is hidden from other admins.`
      : mode === 'all' ? `${u.title} is visible to every admin.`
        : `${u.title} is visible to ${picked.length} picked admin${picked.length === 1 ? '' : 's'}.` });
    try { window.dispatchEvent(new Event(ADMIN_ACCESS_CHANGED_EVENT)); } catch { /* not in a browser */ }
    reload();
  };
  const onError = (text: string) => setMsg({ kind: 'error', text });

  const shown = groups.filter(g => matches(g) || (tabsOf[g.key] || []).some(matches));
  const orphanTabs = tabs.filter(t => !groups.some(g => g.key === t.group) && matches(t));

  return (
    <Section title="Admin tabs"
      right={<span className="flex items-center gap-2">
        {(lastBy || lastAt) && (
          <span className="text-[11px] text-grayscale-1100">
            Last change{lastBy ? ` by ${lastBy}` : ''}{lastAt ? ` · ${fmtDate(lastAt)}` : ''}
          </span>
        )}
        <Btn small onClick={reload} disabled={loading}>Refresh</Btn>
      </span>}>
      {readOnly ? (
        <p className="text-xs text-grayscale-1100 -mt-1">Only super admins can change who sees which tab.</p>
      ) : (
        <p className="text-xs text-grayscale-1100 -mt-1">
          Super admins always see every group and sub-tab. Other admins see only what is enabled here, and the
          server refuses the data behind anything hidden. New groups and sub-tabs start hidden until you enable them.
        </p>
      )}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Tag tone="grey">{counts.none} hidden</Tag>
        <Tag tone="amber">{counts.selected} for selected admins</Tag>
        <Tag tone="green">{counts.all} for all admins</Tag>
        <span className="text-grayscale-1100">
          · {admins.filter(a => a.active && !a.superAdmin).length} other active admins
          {admins.some(a => a.superAdmin) && <> · super admins: {admins.filter(a => a.superAdmin).map(a => a.email).join(', ')}</>}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a group or sub-tab"
          aria-label="Find a group or sub-tab"
          className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-full sm:w-64" />
        <ModeSwitch<'all' | 'hidden' | 'new' | 'selected'> label="Show" value={show} onChange={setShow}
          options={[{ value: 'all', label: 'All' }, { value: 'new', label: 'New' },
            { value: 'hidden', label: 'Hidden' }, { value: 'selected', label: 'Selected admins' }]} />
        <label className="flex items-center gap-2 text-xs text-grayscale-1200 sm:ml-auto">
          See as
          <select value={viewAs} onChange={e => setViewAs(e.target.value)} aria-label="See what one admin sees"
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm max-w-[16rem]">
            <option value="">Pick an admin…</option>
            {admins.filter(a => !a.superAdmin).map(a => (
              <option key={a.email} value={a.email}>{a.name ? `${a.name} (${a.email})` : a.email}{a.role === 'team' ? ' · team, read-only' : ''}{a.active ? '' : ' · inactive'}</option>
            ))}
          </select>
        </label>
      </div>
      {viewAs && (
        <div className="text-xs rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-grayscale-1200">
          <b>{viewAs}</b> sees {viewAsGroups} of {groups.length} groups. Their own “Customize views” choices can hide
          more, but never show anything hidden here.
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {loading && !groups.length ? <Loading /> : (
        <div className="flex flex-col divide-y divide-primary-100 border border-primary-200 rounded-lg">
          {shown.length === 0 && orphanTabs.length === 0 && (
            <div className="px-3 py-6 text-sm text-grayscale-1100 text-center">Nothing matches.</div>
          )}
          {shown.map(g => (
            <div key={g.key} className="flex flex-col">
              <UnitRow u={g} admins={admins} viewAs={viewAs} viewAsOpens={viewAs ? opens(g, viewAs) : undefined}
                readOnly={readOnly} onSaved={saved} onError={onError} />
              {(tabsOf[g.key] || []).map(t => (
                <UnitRow key={t.key} u={t} admins={admins} nested parentHidden={g.mode === 'none'}
                  viewAs={viewAs} viewAsOpens={viewAs ? opens(g, viewAs) && opens(t, viewAs) : undefined}
                  readOnly={readOnly} onSaved={saved} onError={onError} />
              ))}
            </div>
          ))}
          {orphanTabs.map(t => (
            <UnitRow key={t.key} u={t} admins={admins} viewAs={viewAs}
              viewAsOpens={viewAs ? opens(t, viewAs) : undefined} readOnly={readOnly} onSaved={saved} onError={onError} />
          ))}
          <div className="px-3 py-3 flex items-start gap-3 flex-wrap bg-grayscale-100 rounded-b-lg">
            <div className="flex flex-col flex-1 min-w-[14rem]">
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-grayscale-1300">Admins &amp; access</span>
                <Tag tone="green">All admins</Tag>
              </span>
              <span className="text-xs text-grayscale-1100">
                Every admin can open it. Only super admins can change anything in it.
              </span>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

const TABS = ['Admin accounts', 'History', 'Admin tabs'];
const TAB_KEYS: Record<string, string> = { accounts: 'Admin accounts', history: 'History', tabs: 'Admin tabs' };

// Deep links: tab=accounts | history (with email=) | tabs.
const AccessControlPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState<string>(() => TAB_KEYS[String(target?.tab || '')] || TABS[0]);
  const [historyEmail, setHistoryEmail] = useState(String(target?.email || '').toLowerCase());
  useEffect(() => {
    if (!target) return;
    const t = TAB_KEYS[String(target.tab || '')];
    if (t) setTab(t);
    if (target.email) setHistoryEmail(String(target.email).toLowerCase());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Admin accounts' && (
        <AdminAccounts onHistory={email => { setHistoryEmail(email); setTab('History'); }} />
      )}
      {tab === 'History' && <AdminHistory email={historyEmail} onEmail={setHistoryEmail} />}
      {tab === 'Admin tabs' && <AdminTabsPage />}
    </div>
  );
};

const AdminTabsPage: React.FC = () => {
  const [st, setSt] = useState<{ loading: boolean; error: string; data: any }>({ loading: true, error: '', data: null });
  const load = useCallback(async () => {
    setSt(s => ({ ...s, loading: true, error: '' }));
    const { data, error } = await panelGet('access', 'access_state');
    setSt(s => ({ loading: false, error, data: data ?? s.data }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const admins = useMemo(() => readAdmins(st.data?.admins,
    Array.isArray(st.data?.super_admins) ? st.data.super_admins.map(String) : KNOWN_SUPER_ADMINS), [st.data]);

  return (
    <div className="flex flex-col gap-4">
      {st.error && (
        <Msg kind="error">
          {st.data ? `Could not refresh: ${st.error}` : st.error}
        </Msg>
      )}
      {(st.data || st.loading) && (
        <AdminTabs data={st.data} loading={st.loading} reload={load} admins={admins} />
      )}
      {!st.data && !st.loading && (
        <div><Btn small onClick={load}>Retry</Btn></div>
      )}
    </div>
  );
};

export default AccessControlPanel;
